import { useMemo } from 'react';
import { CutsGroup, WizardPrimalCut } from '../types';

export function useCutGroups(selectedIds: string[], cutGroups: CutsGroup[]) {
  /**
   * Determine if a cut group should use radio buttons (mutually exclusive) or checkboxes.
   *
   * A group is mutually exclusive if ALL pairs of cuts within the group block each other.
   * Example: In the Short Loin group, you might have:
   * - T-Bone (blocks NY Strip + Filet)
   * - Porterhouse (blocks NY Strip + Filet)
   * - NY Strip (blocks T-Bone + Porterhouse)
   * - Filet Mignon (blocks T-Bone + Porterhouse)
   *
   * If every cut blocks every other cut in the group, we show radio buttons (choose only one).
   * Otherwise, we show checkboxes (choose multiple).
   */
  const isMutuallyExclusiveGroup = useMemo(
    () => (group: CutsGroup) => {
      const ids = group.cuts.map((cut) => cut._id);
      if (ids.length < 2) return false; // Single option groups should use checkboxes

      // Build a map of which cuts block which other cuts
      const blockedByMap = new Map<string, Set<string>>();
      group.cuts.forEach((cut) => {
        const blockers = new Set(
          (cut.blockedBy ?? [])
            .map((blocker) => blocker?.primalCutId)
            .filter(
              (id): id is string => !!id && id !== cut._id && ids.includes(id),
            ),
        );
        blockedByMap.set(cut._id, blockers);
      });

      // Check every pair: if ANY pair doesn't mutually block, it's NOT fully exclusive
      for (let i = 0; i < ids.length; i++) {
        for (let j = i + 1; j < ids.length; j++) {
          const cutA = ids[i];
          const cutB = ids[j];
          const cutBBlockscutA = blockedByMap.get(cutA)?.has(cutB) ?? false;
          const cutABlockscutB = blockedByMap.get(cutB)?.has(cutA) ?? false;
          if (!cutBBlockscutA && !cutABlockscutB) return false;
        }
      }
      return true;
    },
    [cutGroups],
  );

  /**
   * Check if a cut is currently disabled because a blocking cut is selected.
   * Example: If customer already selected T-Bone, then NY Strip and Filet Mignon are disabled.
   */
  const isDisabled = useMemo(
    () => (cut: WizardPrimalCut) =>
      (cut.blockedBy ?? []).some((b) => selectedIds.includes(b?.primalCutId)),
    [selectedIds],
  );

  /**
   * Generate user-friendly text explaining why a cut is blocked.
   * Returns: "Blocked By: T-Bone Steak, Porterhouse Steak" or undefined if not blocked.
   */
  const getBlockedByText = useMemo(
    () =>
      (cut: WizardPrimalCut): string | undefined => {
        if (cut.blockedBy?.length === 0) return undefined;
        const blockingCuts = (cut.blockedBy ?? [])
          .filter((b) => selectedIds.includes(b?.primalCutId))
          .map((b) => b?.name)
          .filter(Boolean);

        if (blockingCuts.length === 0) return undefined;

        return `Blocked By: ${blockingCuts.join(', ')}`;
      },
    [selectedIds],
  );

  return {
    isMutuallyExclusiveGroup,
    isDisabled,
    getBlockedByText,
  };
}
